"use client";

import React from "react";
import { ShoppingBag, ClipboardCheck, Package, Truck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  id: 1,
  title: "Discovery Call",
  description: "Within 24 hours of your inquiry, we'll schedule a detailed consultation to discuss your vision, budget, and the unique requirements of your Denver home.",
  icon: ShoppingBag
}, {
  id: 2,
  title: "On-site Visit",
  description: "Our master craftsmen visit your property to take precise measurements and assess structural possibilities, ensuring our design aligns with your home's reality.",
  icon: ClipboardCheck
}, {
  id: 3,
  title: "3D Modeling & Design",
  description: "Visualize your transformation before a single hammer swings. We create immersive 3D renderings and floor plans that bring your future space to life.",
  icon: Package
}, {
  id: 4,
  title: "Construction Kickoff",
  description: "With permits secured and materials sourced, our licensed team begins the build phase, maintaining a clean site and professional communication throughout.",
  icon: Truck
}];
function TimelineStage({
  stage,
  index,
  isEven
}: {
  stage: typeof stages[0];
  index: number;
  isEven: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const Icon = stage.icon;
  return <div ref={ref} data-index={index} className="relative flex w-full flex-col items-start md:flex-row md:items-center md:justify-between">
      {}
      <div className={`absolute left-8 z-10 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-md bg-background border-4 shadow-md transition-colors duration-700 md:left-1/2 ${isVisible ? "border-primary" : "border-border"}`}>
        <Icon className={`h-5 w-5 transition-colors duration-700 ${isVisible ? "text-primary" : "text-muted-foreground"}`} />
      </div>

      {}
      <div className={`w-full pl-24 transition-all duration-700 md:w-[45%] md:pl-0 ${isEven ? "md:mr-auto md:pr-16 md:text-right" : "md:ml-auto md:pl-16 md:text-left"} ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <Card className="border-border/50 shadow-md transition-shadow duration-300 hover:shadow-lg bg-card">
          <CardHeader className={`pb-2 ${isEven ? "md:items-end" : "md:items-start"}`}>
            <CardTitle className="font-default text-xl text-foreground font-semibold tracking-tight">{stage.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="font-default text-base text-muted-foreground leading-relaxed">
              {stage.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function FromStoreToDoor() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="246437" id="what-to-expect-next" className="dark relative overflow-hidden bg-background py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-20 text-center">
          <h2 className="font-default mb-4 text-3xl font-bold text-foreground md:text-5xl tracking-tighter">
            What to Expect Next
          </h2>
          <p className="font-default mx-auto max-w-2xl text-lg text-muted-foreground">
            Our refined process ensures your high-end remodeling project moves from initial concept to finished masterpiece with absolute precision.
          </p>
        </div>

        {}
        <div className="relative py-4" ref={sectionRef}>
          {}
          <div className="absolute bottom-0 left-8 top-0 w-1 -translate-x-1/2 rounded-md bg-border md:left-1/2" />

          {}
          <div className="absolute left-8 top-0 w-1 -translate-x-1/2 rounded-md bg-primary transition-all ease-out md:left-1/2" style={{
          height: sectionVisible ? "100%" : "0%",
          transitionDuration: "2500ms"
        }} />

          {}
          <div className="flex flex-col space-y-16 md:space-y-24">
            {stages.map((stage, index) => <TimelineStage key={stage.id} stage={stage} index={index} isEven={index % 2 === 0} />)}
          </div>
        </div>
      </div>
    </section>;
}